// ./src/hooks/useEventGuests.ts
//
// Loads an event's guests / attendees / team (paginated, filterable, searchable).
// Everyone at an event is an `event_guest` row distinguished by guestRole, so the
// same hook backs both the Guests page and the Team page — the page supplies the
// role scope through the filter. Mirrors usePersons (loadMore / refresh / bulk).

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  EventGuest,
  EventGuestService,
  EventGuestFilter,
  EventGuestInput,
  EventGuestBulkInput,
  Pagination,
  SortItem,
  listPage,
} from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// ============================================
// Types
// ============================================

export interface UseEventGuestsOptions {
  pageSize?: number;
  initialFilters?: Partial<EventGuestFilter>;
  initialSort?: SortItem[];
  autoLoad?: boolean;
  /**
   * ROADMAP #177 — read each guest's `arrival` too (the guest list). Off everywhere else: it costs a ticket read and a
   * plan check per page. ⚠ A mutation's answer carries no `arrival` — the page keeps the last one it read.
   */
  withArrival?: boolean;
  /** Each guest's `ticketCount` on every read of the list (the Guests page). */
  withTicketCount?: boolean;
}

export interface UseEventGuestsReturn {
  guest: EventGuest | null;
  guests: EventGuest[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  isCreating: boolean;
  isUpdating: boolean;
  isBulkUpdating: boolean;
  removingIds: Set<string>;
  isRemoving: (id?: string) => boolean;

  filters: Partial<EventGuestFilter>;
  sortBy: SortItem[];

  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<EventGuestFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: EventGuestInput) => Promise<OpResult>;
  update: (id: string, params: EventGuestInput) => Promise<OpResult>;
  setRsvp: (
    id: string,
    changes: { rsvpStatus?: string; partySize?: number; rsvpGroupSize?: number; isAttendee?: boolean },
  ) => Promise<OpResult>;
  /** Record one review's outcome; the server stamps the audit and emails each contactable guest. */
  decide: (params: {
    approveIds?: string[];
    denyIds?: string[];
    reason?: string | null;
    shareReason?: boolean;
  }) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;
  /** Mass-change the bulk-writable fields (RSVP status / role / star / status) on many guests. */
  bulkUpdate: (ids: string[], params: EventGuestBulkInput) => Promise<OpResult>;

  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;
  /**
   * #228 — re-read the rows ON SCREEN, quietly — what a live page's timer calls (the Arrivals page, while visible): every
   * page loaded so far in one read, no loading state, nothing dropped by a "Load more". It gives way to anything else: a
   * read or a change made meanwhile wins, and a failed read leaves the rows as they are (the next tick tries again).
   */
  reReadShown: () => Promise<void>;

  reset: () => void;
  getById: (id: string | undefined) => EventGuest | undefined;

  totalCount: number;

  lastError: OpResult | null;
  /**
   * #177 — the LIST read's own failure (null once a read succeeds). ⚠ A page must show it: a rejected read empties the
   * rows, and an empty list reads as "nobody yet" / "no match" — the Arrivals page said "Nobody has arrived yet" over a
   * read the gateway had refused (memory: a rejected query looks empty). `lastError` also carries mutations' failures.
   */
  listError: OpResult | null;
  clearError: () => void;
}

// ============================================
// Defaults
// ============================================

const DEFAULT_SORT: SortItem[] = [
  { name: "lastName", order: "ASC" },
  { name: "firstName", order: "ASC" },
];

const DEFAULT_FILTERS: Partial<EventGuestFilter> = {};

// ============================================
// Hook
// ============================================

export const useEventGuests = (options: UseEventGuestsOptions = {}): UseEventGuestsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    withArrival = false,
    withTicketCount = false,
  } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;

  const pageSizeRef = useRef(pageSize);
  // #177 — fixed for the hook's life (which read the page asks for), so the list callback stays stable.
  const listFnRef = useRef(withArrival ? EventGuestService.listWithArrival : withTicketCount ? EventGuestService.listWithTicketCount : EventGuestService.list);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  const lastQueriedFilterRef = useRef<Partial<EventGuestFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  const listCallIdRef = useRef(0);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  const [guest, setGuest] = useState<EventGuest | null>(null);
  const [guests, setGuests] = useState<EventGuest[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const [filters, setFilters] = useState<Partial<EventGuestFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  const [currentPage, setCurrentPage] = useState(1);
  // #228 — the rows as last rendered: a quiet re-read that finds them changed when it lands (a save, a read) gives way.
  const guestsRef = useRef<EventGuest[]>(guests);
  guestsRef.current = guests;

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const [lastError, setLastError] = useState<OpResult | null>(null);
  const [listError, setListError] = useState<OpResult | null>(null);

  const isEmpty = useMemo(() => isLoaded && guests.length === 0, [isLoaded, guests.length]);

  // ── Removal tracking ──
  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const isRemoving = useCallback(
    (id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0),
    [removingIds]
  );

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setGuest(null);
    setGuests([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setIsBulkUpdating(false);
    setRemovingIds(new Set());
  }, []);

  // ── Core reads ──
  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await EventGuestService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as EventGuest[])[0];
        setGuest(data);
        setGuests((prev) => upsertBy(prev, data));
        setLastError(null);

        return result;
      }

      setLastError(result);
      setGuest(null);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setGuest(null);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  const list = useCallback(
    async (
      filterOverride: Partial<EventGuestFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);

      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const result = await listPage(
          listFnRef.current,
          filterOverride as EventGuestFilter,
          { page: targetPage, pageSize: targetPageSize },
          currentSortBy
        );

        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data) ? (result.data as EventGuest[]) : [];

          if (isAppending) {
            setGuests((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setGuests(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);
          setListError(null);

          return result;
        }

        setLastError(result);
        setListError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setGuests([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setListError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setGuests([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  const getMany = useCallback(async (ids: string[]): Promise<OpResult> => {
    if (ids.length === 0) {
      return new OpResult();
    }

    setLoading(true);
    setLastError(null);

    try {
      const result = await EventGuestService.getMany(ids);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const loadedItems = Array.isArray(result.data) ? (result.data as EventGuest[]) : [];
        setGuests((prev) => upsertManyBy(prev, loadedItems));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Mutations ──
  const create = useCallback(async (params: EventGuestInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await EventGuestService.create(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newItem = (result.data as EventGuest[])[0];
        setGuest(newItem);
        setGuests((prev) => upsertBy(prev, newItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(async (id: string, params: EventGuestInput): Promise<OpResult> => {
    if (!id) {
      const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Guest ID is required for updating");
      setLastError(result);

      return result;
    }

    setIsUpdating(true);
    setLastError(null);

    try {
      const result = await EventGuestService.update({ id }, params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updatedItem = (result.data as EventGuest[])[0];
        setGuest(updatedItem);
        setGuests((prev) => upsertBy(prev, updatedItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  // Crew RSVP change — set only a guest's day-of RSVP state (status / party size /
  // rsvp-group headcount / attending) via the dedicated `eventGuestSetRsvp` mutation.
  // This is the write path for a crew member with `guests.rsvp` (but not `guests.edit`);
  // the generic `update` is owner/full-edit only server-side, so RSVP changes made in
  // crew mode must go through here. Patches local state from the returned row like update.
  const setRsvp = useCallback(
    async (
      id: string,
      changes: { rsvpStatus?: string; partySize?: number; rsvpGroupSize?: number; isAttendee?: boolean },
    ): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Guest ID is required for updating");
        setLastError(result);

        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await EventGuestService.setRsvp(id, changes);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as EventGuest[])[0];
          setGuest(updatedItem);
          setGuests((prev) => upsertBy(prev, updatedItem));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  // Approve / deny a request to attend (ROADMAP #37). Mirrors setRsvp — a dedicated mutation
  // rather than a status write, because the server also stamps the decision audit and emails the
  // guest. Takes one id or many so the guest drawer and the bulk bar share one path. Patches every
  // returned row into local state; a guest already in the target state comes back unchanged (the
  // server is non-clobbering), which is exactly what we want to render.
  const decide = useCallback(
    async (params: {
      approveIds?: string[];
      denyIds?: string[];
      reason?: string | null;
      shareReason?: boolean;
    }): Promise<OpResult> => {
      const total = (params.approveIds?.length ?? 0) + (params.denyIds?.length ?? 0);
      if (total === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Mark at least one guest approved or denied");
        setLastError(result);

        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await EventGuestService.decide(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItems = result.data as EventGuest[];
          setGuests((prev) => upsertManyBy(prev, updatedItems));
          // Keep the open drawer's guest in step when it was one of the decided.
          setGuest((prev) => (prev ? updatedItems.find((g) => g.id === prev.id) ?? prev : prev));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Guest ID is required for removal");
        setLastError(result);

        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await EventGuestService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setGuest(null);
          setGuests((prev) => removeBy(prev, id));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one guest must be selected");
        setLastError(result);

        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await EventGuestService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setGuest(null);
          setGuests((prev) => removeManyBy(prev, ids));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  const bulkUpdate = useCallback(
    async (ids: string[], params: EventGuestBulkInput): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one guest must be selected");
        setLastError(result);

        return result;
      }

      setIsBulkUpdating(true);
      setLastError(null);

      try {
        const result = await EventGuestService.bulkUpdate(ids, params);

        if (result.code === OP_RESULT_CODES.OK) {
          const updatedItems = Array.isArray(result.data) ? (result.data as EventGuest[]) : [];
          setGuests((prev) => upsertManyBy(prev, updatedItems));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsBulkUpdating(false);
      }
    },
    []
  );

  // ── Convenience ──
  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: lastPageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  const reReadShown = useCallback(async (): Promise<void> => {
    if (!isLoaded || loading || loadingMore) {
      return;
    }

    const callAtStart = listCallIdRef.current;
    const rowsAtStart = guestsRef.current;
    const pageSizeShown = lastPageSizeRef.current;
    const rowsShown = pageSizeShown * Math.max(1, currentPage);

    try {
      const result = await listPage(listFnRef.current, lastQueriedFilterRef.current as EventGuestFilter, { page: 1, pageSize: rowsShown }, lastQueriedSortRef.current);

      // A read the user started, or a change made here meanwhile (a save, a removal), is newer than this answer.
      if (listCallIdRef.current !== callAtStart || guestsRef.current !== rowsAtStart) {
        return;
      }

      if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
        const rows = result.data as EventGuest[];
        setGuests(rows);
        setHasMore(rows.length === rowsShown);
      }
    } catch {
      // Left for the next tick: the rows on screen stay, and no error is raised over a list that is still right.
    }
  }, [isLoaded, loading, loadingMore, currentPage]);

  const getById = useCallback(
    (id: string | undefined): EventGuest | undefined => {
      if (!id) {
        return undefined;
      }

      return guests.find((item) => item.id === id);
    },
    [guests]
  );

  const totalCount = useMemo(() => guests.length, [guests]);

  // ── Effects ──
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }

      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ── Return ──
  return useMemo(
    () => ({
      guest,
      guests,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      setRsvp,
      decide,
      remove,
      removeMany,
      bulkUpdate,
      loadMore,
      refresh,
      reReadShown,
      reset,
      getById,
      totalCount,
      lastError,
      listError,
      clearError,
    }),
    [
      guest,
      guests,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      setRsvp,
      decide,
      remove,
      removeMany,
      bulkUpdate,
      loadMore,
      refresh,
      reReadShown,
      reset,
      getById,
      totalCount,
      lastError,
      listError,
      clearError,
    ]
  );
};
